import { useCallback, useMemo } from 'react';

import { useStudentInvoicesQuery } from 'core';

export interface IStudentInvoice {
  fakturowniaId: string | null;
  number: string | null;
  status: string | null;
  issueDate: string | null;
  paymentTo: string | null;
  currency: string | null;
  priceGross: number | null;
  paid: number | null;
  outstanding: number | null;
  isOverdue: boolean | null;
  daysOverdue: number | null;
  viewUrl: string | null;
  paymentUrl: string | null;
  /** ТЗ 0044: у фактуры есть корректировка — суммы выше уже нетто. */
  isCorrected: boolean | null;
  correctionReason: string | null;
  /** ТЗ 0074: из-за этой фактуры доступ к занятиям приостановлен. */
  isBlocking: boolean;
  /** ТЗ 0101: открытая проформа — фактуру VAT выставят после оплаты. */
  isProforma: boolean;
}

interface IUseMyInvoices {
  invoices: IStudentInvoice[];
  loading: boolean;
  error: string;
  refetch: () => void;
}

/**
 * Reads the logged-in student's own issued invoices (paid + unpaid) via the
 * `myInvoices` query. `cache-and-network` so a freshly issued/paid invoice
 * surfaces on the next mount without a hard reload.
 *
 * ТЗ 0074: фактуры, держащие приостановку доступа, идут ПЕРВЫМИ — студент
 * видит сразу, что именно оплатить. Остальной порядок — как отдал сервер
 * (сортировка стабильная).
 */
export const useMyInvoices = (): IUseMyInvoices => {
  const { data, loading, error, refetch } = useStudentInvoicesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const invoices = useMemo(() => {
    const rows = (data?.myInvoices ?? []).filter(Boolean) as IStudentInvoice[];
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => Number(b.row.isBlocking) - Number(a.row.isBlocking) || a.index - b.index)
      .map(({ row }) => row);
  }, [data?.myInvoices]);

  const refetchInvoices = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    invoices,
    loading,
    error: error?.message || '',
    refetch: refetchInvoices,
  };
};
